// src/components/admin/AdminDepartmentsPage.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Container, Grid, Paper, Typography, Box, Card, CardContent,
  IconButton, Button, Chip, Skeleton, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  Switch,
  FormControlLabel, Divider, List, ListItem, ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Business, Add, Edit, Delete,
  Star, CheckCircle, Cancel
} from '@mui/icons-material';

import { adminApi } from '../../api/adminApi';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -20 }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.6
};

export default function AdminDepartmentsPage() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dialog states
  const [departmentDialog, setDepartmentDialog] = useState(false);
  const [phoneDialog, setPhoneDialog] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedPhone, setSelectedPhone] = useState(null);

  // Form states
  const [departmentForm, setDepartmentForm] = useState({
    name: '',
    keywords: '',
    emails: '',
    client: ''
  });

  const [phoneForm, setPhoneForm] = useState({
    phone_number: '',
    name: '',
    is_primary: false,
    is_active: true
  });

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await adminApi.getDepartments();
      setDepartments(data.departments || data);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError(err.message || 'فشل في تحميل الأقسام');
    } finally {
      setLoading(false);
    }
  };

  const handleDepartmentSubmit = async () => {
    try {
      if (selectedDepartment) {
        await adminApi.updateDepartment(selectedDepartment.id, departmentForm);
      } else {
        await adminApi.createDepartment(departmentForm);
      }
      setDepartmentDialog(false);
      resetDepartmentForm();
      fetchDepartments();
    } catch (err) {
      console.error('Error saving department:', err);
      setError(err.message || 'فشل في حفظ القسم');
    }
  };

  const handlePhoneSubmit = async () => {
    try {
      if (selectedPhone) {
        await adminApi.updateDepartmentPhone(selectedDepartment.id, selectedPhone.id, phoneForm);
      } else {
        await adminApi.createDepartmentPhone(selectedDepartment.id, phoneForm);
      }
      setPhoneDialog(false);
      resetPhoneForm();
      fetchDepartments();
    } catch (err) {
      console.error('Error saving phone number:', err);
      setError(err.message || 'فشل في حفظ رقم الهاتف');
    }
  };

  const handleDeleteDepartment = async (departmentId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا القسم؟')) {
      try {
        await adminApi.deleteDepartment(departmentId);
        fetchDepartments();
      } catch (err) {
        console.error('Error deleting department:', err);
        setError(err.message || 'فشل في حذف القسم');
      }
    }
  };

  const handleDeletePhone = async (departmentId, phoneId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الرقم؟')) {
      try {
        await adminApi.deleteDepartmentPhone(departmentId, phoneId);
        fetchDepartments();
      } catch (err) {
        console.error('Error deleting phone number:', err);
        setError(err.message || 'فشل في حذف رقم الهاتف');
      }
    }
  };

  const resetDepartmentForm = () => {
    setDepartmentForm({
      name: '',
      keywords: '',
      emails: '',
      client: ''
    });
    setSelectedDepartment(null);
  };

  const resetPhoneForm = () => {
    setPhoneForm({
      phone_number: '',
      name: '',
      is_primary: false,
      is_active: true
    });
    setSelectedPhone(null);
  };

  const openDepartmentDialog = (department = null) => {
    if (department) {
      setDepartmentForm({
        name: department.name,
        keywords: department.keywords,
        emails: department.emails || '',
        client: department.client
      });
      setSelectedDepartment(department);
    } else {
      resetDepartmentForm();
    }
    setDepartmentDialog(true);
  };

  const openPhoneDialog = (department, phone = null) => {
    setSelectedDepartment(department);
    if (phone) {
      setPhoneForm({
        phone_number: phone.phone_number,
        name: phone.name,
        is_primary: phone.is_primary,
        is_active: phone.is_active
      });
      setSelectedPhone(phone);
    } else {
      resetPhoneForm();
    }
    setPhoneDialog(true);
  };

  const DepartmentCard = ({ department }) => (
    <Card
      component={motion.div}
      whileHover={{ y: -4 }}
      sx={{
        height: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business color="primary" />
            <Typography variant="h6" fontWeight="600">
              {department.name}
            </Typography>
          </Box>
          <Box>
            <IconButton
              size="small"
              onClick={() => openDepartmentDialog(department)}
              sx={{ color: 'primary.main' }}
            >
              <Edit />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDeleteDepartment(department.id)}
              sx={{ color: 'error.main' }}
            >
              <Delete />
            </IconButton>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          📧 {department.emails || 'لا يوجد إيميل'}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            الكلمات المفتاحية:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {department.keywords?.split(',').map((keyword, index) => (
              <Chip
                key={index}
                label={keyword.trim()}
                size="small"
                variant="outlined"
                color="primary"
              />
            ))}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="600">
            أرقام الهاتف
          </Typography>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => openPhoneDialog(department)}
            variant="outlined"
            color="primary"
          >
            إضافة رقم
          </Button>
        </Box>

        <List dense>
          {department.phone_numbers?.map((phone) => (
            <ListItem key={phone.id} sx={{ px: 0 }}>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight="500">
                      {phone.name}
                    </Typography>
                    {phone.is_primary && <Star color="warning" fontSize="small" />}
                    {phone.is_active ? (
                      <CheckCircle color="success" fontSize="small" />
                    ) : (
                      <Cancel color="error" fontSize="small" />
                    )}
                  </Box>
                }
                secondary={phone.phone_number}
              />
              <ListItemSecondaryAction>
                <IconButton
                  size="small"
                  onClick={() => openPhoneDialog(department, phone)}
                >
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDeletePhone(department.id, phone.id)}
                  sx={{ color: 'error.main' }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>

        {(!department.phone_numbers || department.phone_numbers.length === 0) && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            لا توجد أرقام هواتف
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="text" width="60%" height={32} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid key={item} xs={12} md={6} lg={4}>
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
    >
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="700" gutterBottom>
            إدارة الأقسام وأرقام الهاتف
          </Typography>
          <Typography variant="body1" color="text.secondary">
            إدارة أقسام الدعم الفني وأرقام الهاتف الخاصة بكل قسم
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight="600">
              الأقسام ({departments.length})
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => openDepartmentDialog()}
            sx={{ borderRadius: 2 }}
          >
            إضافة قسم جديد
          </Button>
        </Box>

        <Grid container spacing={3}>
          {departments.map((department) => (
            <Grid key={department.id} xs={12} md={6} lg={4}>
              <DepartmentCard department={department} />
            </Grid>
          ))}
        </Grid>

        {departments.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Business sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              لا توجد أقسام
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              ابدأ بإضافة قسم جديد لإدارة أرقام الهاتف
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => openDepartmentDialog()}
            >
              إضافة قسم جديد
            </Button>
          </Paper>
        )}

        {/* Department Dialog */}
        <Dialog open={departmentDialog} onClose={() => setDepartmentDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {selectedDepartment ? 'تعديل القسم' : 'إضافة قسم جديد'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="اسم القسم"
                value={departmentForm.name}
                onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="عناوين البريد الإلكتروني"
                value={departmentForm.emails}
                onChange={(e) => setDepartmentForm({ ...departmentForm, emails: e.target.value })}
                helperText="أضف إيميلات متعددة مفصولة بفاصلة. مثال: support@company.com, manager@company.com"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="الكلمات المفتاحية (مفصولة بفاصلة)"
                value={departmentForm.keywords}
                onChange={(e) => setDepartmentForm({ ...departmentForm, keywords: e.target.value })}
                helperText="مثال: دعم,مساعدة,مشكلة,عطل"
                sx={{ mb: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDepartmentDialog(false)}>
              إلغاء
            </Button>
            <Button onClick={handleDepartmentSubmit} variant="contained">
              {selectedDepartment ? 'تحديث' : 'إضافة'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Phone Number Dialog */}
        <Dialog open={phoneDialog} onClose={() => setPhoneDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {selectedPhone ? 'تعديل رقم الهاتف' : 'إضافة رقم هاتف'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="رقم الهاتف"
                value={phoneForm.phone_number}
                onChange={(e) => setPhoneForm({ ...phoneForm, phone_number: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="اسم الرقم"
                value={phoneForm.name}
                onChange={(e) => setPhoneForm({ ...phoneForm, name: e.target.value })}
                helperText="مثال: رقم رئيسي، رقم احتياطي"
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={phoneForm.is_primary}
                    onChange={(e) => setPhoneForm({ ...phoneForm, is_primary: e.target.checked })}
                  />
                }
                label="رقم رئيسي"
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={phoneForm.is_active}
                    onChange={(e) => setPhoneForm({ ...phoneForm, is_active: e.target.checked })}
                  />
                }
                label="رقم نشط"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPhoneDialog(false)}>
              إلغاء
            </Button>
            <Button onClick={handlePhoneSubmit} variant="contained">
              {selectedPhone ? 'تحديث' : 'إضافة'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </motion.div>
  );
}
